/**
 * Resolve top-corner sidebar identity for relationship portals,
 * project assignment shells, and organisation account roles.
 *
 * Display-only: never invents project assignments or changes authority.
 * Uses the same relationship-entry resolution as portal nav hrefs.
 *
 * Product hierarchy:
 *   Top heading ? current experience (not an internal account-role name)
 *   Top card    ? organisation / workspace / project / represented entity
 *   Bottom      ? signed-in representative + current operating capacity only
 *   Profile     ? full role and assignment set (not repeated in nav chrome)
 *
 * Stakeholder portals (Investee / Investor / Partner / Vendor):
 *   Heading + footer capacity derive from company?project participation in
 *   the active project (or portfolio) context ? not from a static company-type
 *   field alone. The identity card badge is always Company (entity type),
 *   never Investee/Investor/Partner/Vendor.
 *   Company Portal / Company ? Active / Company Representative remains the
 *   fallback when participation in context is unknown.
 */

import type { User } from '@/types';
import type { OrganizationAccountRole, PlatformAccountRole } from '@/types/authority';
import type { IconName } from '@/components/ui/icons';
import {
  COMPANY_DASHBOARD_LABEL,
  COMPANY_PORTAL_LABEL,
  COMPANY_REPRESENTATIVE_LABEL,
  resolveCompanyPortalEntry,
} from '@/lib/auth/companyPortal';
import { getActiveScopedRelationships } from '@/lib/auth/scopedRelationshipResolver';
import { isOrganizationAccountRole, resolveIdentityRoles } from '@/lib/auth/identityRoles';
import { getMvpAccountRoleLabel } from '@/lib/auth/accountRoles';
import {
  deriveCompanyParticipation,
  scopedTypeToProjectAssignment,
  type CompanyProjectParticipation,
} from '@/types/projectParticipants';
import { hasScopedAccessLevel } from '@/types';

const PARTNER_ASSIGNED_WORK_LABEL = 'Partner Assigned Work';
const VENDOR_ASSIGNED_WORK_LABEL = 'Vendor Assigned Work';
const PARTNER_REPRESENTATIVE_LABEL = 'Partner Representative';
const VENDOR_REPRESENTATIVE_LABEL = 'Vendor Representative';

export type RelationshipPortalKind =
  | 'company'
  | 'investee'
  | 'investor'
  | 'partner'
  | 'vendor';

export type OrganizationSidebarKind = OrganizationAccountRole;

/** Project-assignment operating shells. */
export type ProjectAssignmentSidebarKind = 'project_manager' | 'project_contributor' | 'observer';

export type SidebarIdentityKind =
  | RelationshipPortalKind
  | OrganizationSidebarKind
  | PlatformAccountRole
  | ProjectAssignmentSidebarKind;

/** Relationship portals keep person+entity; organisation/project shells are context-first. */
export type SidebarIdentityPresentation = 'relationship' | 'organization';

export interface PortalSidebarIdentityStatus {
  label: string;
  dot: string;
  text: string;
}

export interface PortalSidebarIdentity {
  kind: SidebarIdentityKind;
  portalLabel: string;
  /** Primary title: represented entity, organisation, or current project. */
  displayName: string;
  /** Secondary entity line ? optional project/context under the entity. */
  organizationName: string | null;
  /** Type badge ? relationship portals only; null for organisation/project cards. */
  typeLabel: string | null;
  /**
   * Workspace/project context before status (organisation presentation), e.g.
   * "Organisation account" / "Workspace" / "Project". Null for relationship portals.
   */
  workspaceLabel: string | null;
  /**
   * Current operating capacity under the signed-in person's name (footer).
   * Account role for org shells; assignment/representative label for scoped shells.
   */
  capacityLabel: string;
  status: PortalSidebarIdentityStatus;
  icon: IconName;
  entityId: string;
  presentation: SidebarIdentityPresentation;
}

/** Optional route/demo context for project-assignment and stakeholder shells. */
export interface SidebarIdentityContext {
  projectId?: string | null;
  projectName?: string | null;
  /**
   * Active company?project participation for the represented company in the
   * current project/portfolio context (Investee / Investor / Partner / Vendor).
   * When set, stakeholder portal labels prefer this over generic Company Portal.
   */
  companyParticipation?: CompanyProjectParticipation | null;
  /** Explicit UX context selected from the application switcher. */
  navigationContextKind?: 'account' | 'company' | 'partner' | 'vendor' | null;
  /** Exact represented entity selected when a user holds several relationships. */
  navigationContextResourceId?: string | null;
}

export interface StakeholderPortalTerminology {
  portalLabel: string;
  dashboardLabel: string;
  representativeLabel: string;
  projectsTitle: string;
  /** Lower-case phrase for prose, e.g. "investee company". */
  representedEntityLabel: string;
}

const STAKEHOLDER_PROJECT_SCOPE_TYPES = new Set<string>([
  'company_representative',
  'partner_representative',
  'vendor_representative',
  'investor_representative',
]);

/**
 * Extract `/projects/:id` from the current pathname when present.
 * Works for workspace and portal trees (`/companies/:id/projects/:projectId`).
 */
export function resolveSidebarRouteProjectId(pathname: string | null | undefined): string | null {
  if (!pathname) return null;
  const match = pathname.match(/\/projects\/([^/?#]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Resolve the active project id for sidebar identity chrome.
 *
 * Precedence:
 *   1. Route `/projects/:id`
 *   2. Active project assignment scope (Manager / Contributor / Observer)
 *   3. Active stakeholder representation scoped to a project
 *      (`company_representative` / partner / vendor / investor with
 *      resourceType=project) ? covers company-portal dashboard/home when the
 *      representative's operating context is a known project participation
 */
export function resolveSidebarScopedProjectId(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  routeProjectId: string | null | undefined = null,
): string | null {
  const fromRoute = routeProjectId?.trim() || null;
  if (fromRoute) return fromRoute;

  const relationships = user?.scopedRelationships ?? [];

  const projectAssignmentId = relationships.find(
    (relationship) =>
      ['project_manager', 'project_contributor', 'observer'].includes(relationship.type)
      && relationship.status === 'active'
      && relationship.resourceType === 'project'
      && Boolean(relationship.resourceId),
  )?.resourceId;
  if (projectAssignmentId) return projectAssignmentId;

  const stakeholderProjectId = relationships.find(
    (relationship) =>
      STAKEHOLDER_PROJECT_SCOPE_TYPES.has(relationship.type)
      && relationship.status === 'active'
      && relationship.resourceType === 'project'
      && Boolean(relationship.resourceId),
  )?.resourceId;

  return stakeholderProjectId ?? null;
}

const STATUS_ACTIVE: PortalSidebarIdentityStatus = {
  label: 'Active',
  dot: 'bg-status-success',
  text: 'text-status-success-foreground',
};

const STATUS_PENDING: PortalSidebarIdentityStatus = {
  label: 'Pending',
  dot: 'bg-status-warning',
  text: 'text-status-warning-foreground',
};

const STATUS_INACTIVE: PortalSidebarIdentityStatus = {
  label: 'Inactive',
  dot: 'bg-status-danger',
  text: 'text-status-danger-foreground',
};

/**
 * Top sidebar section labels (above the identity card).
 * Experience names ? never internal account-role tokens as nav-space titles.
 */
const ORGANIZATION_SECTION_LABEL: Readonly<Record<OrganizationAccountRole, string>> = {
  organization_owner: 'Organisation Ownership',
  organization_admin: 'Workspace Administration',
  // Unassigned / bare workspace membership experience (capacity stays Workspace Member).
  organization_member: 'Workspace',
};

/** Workspace status-line prefix ? organisation / workspace, not person role. */
const ORGANIZATION_WORKSPACE_LABEL: Readonly<Record<OrganizationAccountRole, string>> = {
  organization_owner: 'Organisation account',
  // Admin card uses Company type badge (relationship presentation), not this prefix.
  organization_admin: 'Workspace',
  organization_member: 'Workspace',
};

const ORGANIZATION_ICON: Readonly<Record<OrganizationAccountRole, IconName>> = {
  organization_owner: 'crown',
  organization_admin: 'building2',
  organization_member: 'users',
};

const PROJECT_MANAGEMENT_SECTION_LABEL = 'Project Management';
const PROJECT_WORKSPACE_SECTION_LABEL = 'Project Workspace';
const PROJECT_CONTEXT_WORKSPACE_LABEL = 'Project';

/** Card badge for all relationship portals ? entity type, not participation. */
const RELATIONSHIP_CARD_TYPE_LABEL = 'Company';

/**
 * Stakeholder experience labels derived from company?project participation.
 * Card typeLabel is always Company (entity type); participation names appear
 * in portalLabel (heading), dashboardLabel (page title), and capacityLabel (footer).
 */
const PARTICIPATION_PORTAL_LABELS: Readonly<
  Record<
    CompanyProjectParticipation,
    {
      kind: Exclude<RelationshipPortalKind, 'company'>;
      portalLabel: string;
      typeLabel: string;
      dashboardLabel: string;
      capacityLabel: string;
      icon: IconName;
    }
  >
> = {
  Investee: {
    kind: 'investee',
    portalLabel: 'Investee Portal',
    typeLabel: RELATIONSHIP_CARD_TYPE_LABEL,
    dashboardLabel: 'Investee Dashboard',
    capacityLabel: 'Investee Representative',
    icon: 'building2',
  },
  Investor: {
    kind: 'investor',
    portalLabel: 'Investor Portal',
    typeLabel: RELATIONSHIP_CARD_TYPE_LABEL,
    dashboardLabel: 'Investor Dashboard',
    capacityLabel: 'Investor Representative',
    icon: 'briefcase',
  },
  Partner: {
    kind: 'partner',
    portalLabel: PARTNER_ASSIGNED_WORK_LABEL,
    typeLabel: RELATIONSHIP_CARD_TYPE_LABEL,
    dashboardLabel: PARTNER_ASSIGNED_WORK_LABEL,
    capacityLabel: PARTNER_REPRESENTATIVE_LABEL,
    icon: 'handshake',
  },
  Vendor: {
    kind: 'vendor',
    portalLabel: VENDOR_ASSIGNED_WORK_LABEL,
    typeLabel: RELATIONSHIP_CARD_TYPE_LABEL,
    dashboardLabel: VENDOR_ASSIGNED_WORK_LABEL,
    capacityLabel: VENDOR_REPRESENTATIVE_LABEL,
    icon: 'package',
  },
};

function resolveDisplayStatus(
  user: Pick<User, 'status'> | null | undefined,
): PortalSidebarIdentityStatus {
  switch (user?.status) {
    case 'pending':
      return STATUS_PENDING;
    case 'inactive':
    case 'suspended':
      return STATUS_INACTIVE;
    case 'active':
    default:
      return STATUS_ACTIVE;
  }
}

function hasActiveRelationship(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
  type: 'company_representative' | 'partner_representative' | 'vendor_representative',
): boolean {
  return getActiveScopedRelationships(user).some(
    (relationship) =>
      relationship.type === type
      && hasScopedAccessLevel(relationship.accessLevel, 'view'),
  );
}

function resolveRepresentedEntityId(
  user: Pick<User, 'scopedRelationships'>,
  type: 'partner_representative' | 'vendor_representative',
): string | null {
  const relationship = getActiveScopedRelationships(user).find(
    (candidate) => candidate.type === type && hasScopedAccessLevel(candidate.accessLevel, 'view'),
  );
  return relationship?.representedCompanyId ?? relationship?.resourceId ?? null;
}

/**
 * Map a raw company?project relationship_type into sidebar participation labels.
 * Returns null when the relationship does not map to Investee/Investor/Partner/Vendor.
 */
export function resolveParticipationPortalLabels(
  relationshipType: string | null | undefined,
): (typeof PARTICIPATION_PORTAL_LABELS)[CompanyProjectParticipation] | null {
  const participation = deriveCompanyParticipation(relationshipType);
  if (!participation) return null;
  return PARTICIPATION_PORTAL_LABELS[participation];
}

/**
 * Main-content dashboard page title from active company?project participation.
 * Title Case strings; UI may uppercase via CSS (e.g. INVESTEE DASHBOARD).
 * Falls back to Company Dashboard when participation in context is unknown.
 */
export function resolveParticipationDashboardLabel(
  participation: CompanyProjectParticipation | null | undefined,
): string {
  if (!participation) return COMPANY_DASHBOARD_LABEL;
  return PARTICIPATION_PORTAL_LABELS[participation].dashboardLabel;
}

/**
 * Participation-aware language for every represented-company portal surface.
 * The identity card deliberately remains `Company · Active`; these labels
 * describe the company's capacity in the active project context.
 */
export function resolveStakeholderPortalTerminology(
  participation: CompanyProjectParticipation | null | undefined,
): StakeholderPortalTerminology {
  if (!participation) {
    return {
      portalLabel: COMPANY_PORTAL_LABEL,
      dashboardLabel: COMPANY_DASHBOARD_LABEL,
      representativeLabel: COMPANY_REPRESENTATIVE_LABEL,
      projectsTitle: 'Company Projects',
      representedEntityLabel: 'represented company',
    };
  }

  const labels = PARTICIPATION_PORTAL_LABELS[participation];
  const representedEntityLabel = participation === 'Investee'
    ? 'investee company'
    : `${participation.toLowerCase()} company`;

  return {
    portalLabel: labels.portalLabel,
    dashboardLabel: labels.dashboardLabel,
    representativeLabel: labels.capacityLabel,
    projectsTitle: `${participation} Projects`,
    representedEntityLabel,
  };
}

/**
 * Compact project title for the identity card (e.g. Harbour District Mixed-Use
 * Development ? Harbour District). Falls back to the full name.
 */
export function shortProjectDisplayName(projectName: string): string {
  const trimmed = projectName.trim();
  if (!trimmed) return trimmed;
  const withoutProgramme = trimmed
    .replace(/\s+Mixed-Use\s+Development$/i, '')
    .replace(/\s+Regional\s+Infrastructure\s+Programme$/i, '')
    .replace(/\s+Industrial\s+Decarbonisation\s+Facility$/i, '')
    .replace(/\s+European\s+Expansion$/i, '')
    .trim();
  return withoutProgramme || trimmed;
}

type StakeholderChrome = {
  kind: RelationshipPortalKind;
  portalLabel: string;
  typeLabel: string;
  capacityLabel: string;
  icon: IconName;
};

/**
 * Prefer active company?project participation for heading + capacity; fall back
 * to relationship entry defaults. Card typeLabel stays Company either way.
 */
function resolveStakeholderChrome(
  fallback: StakeholderChrome,
  context: SidebarIdentityContext,
): StakeholderChrome {
  const participation = context.companyParticipation;
  if (!participation) {
    return {
      ...fallback,
      typeLabel: RELATIONSHIP_CARD_TYPE_LABEL,
    };
  }
  const labels = PARTICIPATION_PORTAL_LABELS[participation];
  return {
    kind: labels.kind,
    portalLabel: labels.portalLabel,
    typeLabel: RELATIONSHIP_CARD_TYPE_LABEL,
    capacityLabel: labels.capacityLabel,
    icon: labels.icon,
  };
}

/**
 * When the user is a single-entity Company / Partner / Vendor Representative,
 * return identity fields for the sidebar card. Null otherwise (operating shell).
 *
 * Top card = represented entity + Company type badge; footer capacity =
 * representative label. When `context.companyParticipation` is known from the
 * active project, experience heading and capacity follow that participation
 * (Investee Portal / Investee Representative, etc.); the card badge stays Company.
 */
export function resolveRelationshipPortalSidebarIdentity(
  user: User | null | undefined,
  context: SidebarIdentityContext = {},
): PortalSidebarIdentity | null {
  if (!user) return null;

  const entityName = user.company?.trim() || null;
  const status = resolveDisplayStatus(user);

  if (
    (!context.navigationContextKind || context.navigationContextKind === 'company')
    && hasActiveRelationship(user, 'company_representative')
  ) {
    const entry = resolveCompanyPortalEntry(user);
    const selectedCompanyId = context.navigationContextKind === 'company'
      ? context.navigationContextResourceId
      : entry.kind === 'one'
        ? entry.companyId
        : null;
    if (!selectedCompanyId) return null;
    const chrome = resolveStakeholderChrome(
      {
        kind: 'company',
        portalLabel: COMPANY_PORTAL_LABEL,
        typeLabel: RELATIONSHIP_CARD_TYPE_LABEL,
        capacityLabel: COMPANY_REPRESENTATIVE_LABEL,
        icon: 'building2',
      },
      context,
    );
    return {
      kind: chrome.kind,
      portalLabel: chrome.portalLabel,
      displayName: entityName || 'Company',
      organizationName: null,
      typeLabel: chrome.typeLabel,
      workspaceLabel: null,
      capacityLabel: chrome.capacityLabel,
      status,
      icon: chrome.icon,
      entityId: selectedCompanyId,
      presentation: 'relationship',
    };
  }

  if (
    (!context.navigationContextKind || context.navigationContextKind === 'partner')
    && hasActiveRelationship(user, 'partner_representative')
  ) {
    const entityId = context.navigationContextKind === 'partner'
      ? context.navigationContextResourceId
      : resolveRepresentedEntityId(user, 'partner_representative');
    if (!entityId) return null;
    const chrome = resolveStakeholderChrome(
      {
        kind: 'partner',
        portalLabel: PARTNER_ASSIGNED_WORK_LABEL,
        typeLabel: RELATIONSHIP_CARD_TYPE_LABEL,
        capacityLabel: PARTNER_REPRESENTATIVE_LABEL,
        icon: 'handshake',
      },
      context,
    );
    return {
      kind: chrome.kind,
      portalLabel: chrome.portalLabel,
      displayName: entityName || 'Partner',
      organizationName: null,
      typeLabel: chrome.typeLabel,
      workspaceLabel: null,
      capacityLabel: chrome.capacityLabel,
      status,
      icon: chrome.icon,
      entityId,
      presentation: 'relationship',
    };
  }

  if (
    (!context.navigationContextKind || context.navigationContextKind === 'vendor')
    && hasActiveRelationship(user, 'vendor_representative')
  ) {
    const entityId = context.navigationContextKind === 'vendor'
      ? context.navigationContextResourceId
      : resolveRepresentedEntityId(user, 'vendor_representative');
    if (!entityId) return null;
    const chrome = resolveStakeholderChrome(
      {
        kind: 'vendor',
        portalLabel: VENDOR_ASSIGNED_WORK_LABEL,
        typeLabel: RELATIONSHIP_CARD_TYPE_LABEL,
        capacityLabel: VENDOR_REPRESENTATIVE_LABEL,
        icon: 'package',
      },
      context,
    );
    return {
      kind: chrome.kind,
      portalLabel: chrome.portalLabel,
      displayName: entityName || 'Vendor',
      organizationName: null,
      typeLabel: chrome.typeLabel,
      workspaceLabel: null,
      capacityLabel: chrome.capacityLabel,
      status,
      icon: chrome.icon,
      entityId,
      presentation: 'relationship',
    };
  }

  return null;
}

/**
 * Project assignment operating shell: top card = current/assigned project and
 * footer capacity = Project Manager / Contributor / Observer.
 * Account role (Workspace Member) stays out of nav chrome.
 */
export function resolveProjectAssignmentSidebarIdentity(
  user: User | null | undefined,
  context: SidebarIdentityContext = {},
): PortalSidebarIdentity | null {
  if (!user) return null;

  const projectAssignmentScopes = getActiveScopedRelationships(user, 'project').filter(
    (relationship) =>
      ['project_manager', 'project_contributor', 'observer'].includes(relationship.type)
      && hasScopedAccessLevel(relationship.accessLevel, 'view'),
  );

  if (projectAssignmentScopes.length === 0) return null;

  const preferredId = context.projectId?.trim() || null;
  const activeScope =
    (preferredId
      ? projectAssignmentScopes.find((relationship) => relationship.resourceId === preferredId)
      : undefined)
    ?? projectAssignmentScopes[0];

  const projectId = activeScope.resourceId;
  const rawName =
    (preferredId === null || preferredId === projectId
      ? context.projectName?.trim()
      : null)
    || null;
  const displayName = rawName
    ? shortProjectDisplayName(rawName)
    : 'Assigned project';

  const assignmentType = activeScope.type as ProjectAssignmentSidebarKind;
  const capacity = scopedTypeToProjectAssignment(assignmentType) ?? 'Workspace Member';

  return {
    kind: assignmentType,
    portalLabel: assignmentType === 'project_manager'
      ? PROJECT_MANAGEMENT_SECTION_LABEL
      : PROJECT_WORKSPACE_SECTION_LABEL,
    displayName,
    organizationName: null,
    typeLabel: null,
    workspaceLabel: PROJECT_CONTEXT_WORKSPACE_LABEL,
    capacityLabel: capacity,
    status: resolveDisplayStatus(user),
    icon: 'target',
    entityId: projectId,
    presentation: 'organization',
  };
}

/**
 * Organization Governance / Workspace Administration / Workspace experience
 * for the operating shell. Null for platform roles or unknown account roles.
 *
 * Top card identifies the organisation/workspace ? not the signed-in person.
 * Workspace Admin uses Company · Active on the card (entity type badge);
 * Owner/Member keep workspace status-line presentation.
 * For organization_member, portalLabel is the Workspace experience (not the
 * Workspace Member account-role name).
 */
export function resolveOrganizationAccountSidebarIdentity(
  user: User | null | undefined,
): PortalSidebarIdentity | null {
  if (!user) return null;

  const accountRole = user.account_role;
  if (!isOrganizationAccountRole(accountRole)) return null;

  const isOrganizationAdmin = accountRole === 'organization_admin';

  return {
    kind: accountRole,
    portalLabel: ORGANIZATION_SECTION_LABEL[accountRole],
    displayName: user.company?.trim() || 'Organization',
    organizationName: null,
    typeLabel: isOrganizationAdmin ? RELATIONSHIP_CARD_TYPE_LABEL : null,
    workspaceLabel: isOrganizationAdmin ? null : ORGANIZATION_WORKSPACE_LABEL[accountRole],
    capacityLabel: getMvpAccountRoleLabel(accountRole),
    status: resolveDisplayStatus(user),
    icon: ORGANIZATION_ICON[accountRole],
    entityId: user.workspace_id?.trim() || user.id,
    presentation: isOrganizationAdmin ? 'relationship' : 'organization',
  };
}

export function resolvePlatformAccountSidebarIdentity(
  user: User | null | undefined,
): PortalSidebarIdentity | null {
  if (!user) return null;
  const platformRole = resolveIdentityRoles(user).platformRole;
  if (!platformRole) return null;
  return {
    kind: platformRole,
    portalLabel: 'Control Plane',
    displayName: user.company?.trim() || 'AX1 Platform',
    organizationName: null,
    typeLabel: null,
    workspaceLabel: 'Platform context',
    capacityLabel: getMvpAccountRoleLabel(platformRole),
    status: resolveDisplayStatus(user),
    icon: 'server',
    entityId: user.id,
    presentation: 'organization',
  };
}

/**
 * Sidebar top identity precedence:
 *   1. Relationship portal (participation-aware Company / Partner / Vendor)
 *   2. Project assignment shell (Manager / Contributor / Observer)
 *   3. Organisation account-role experience
 */
export function resolveSidebarIdentity(
  user: User | null | undefined,
  context: SidebarIdentityContext = {},
): PortalSidebarIdentity | null {
  if (context.navigationContextKind === 'account') {
    return (
      resolveProjectAssignmentSidebarIdentity(user, context)
      ?? resolveOrganizationAccountSidebarIdentity(user)
      ?? resolvePlatformAccountSidebarIdentity(user)
    );
  }
  return (
    resolveRelationshipPortalSidebarIdentity(user, context)
    ?? resolveProjectAssignmentSidebarIdentity(user, context)
    ?? resolveOrganizationAccountSidebarIdentity(user)
    ?? resolvePlatformAccountSidebarIdentity(user)
  );
}
